import React, { useState } from "react";
import { toast } from "sonner";
import { Lottie } from "lottie-react";
import kyoAnimation from "../animations/kyo_animation1.json";
import axios from "axios";
import { useNavigate } from "react-router-dom";
import TrackingResult from "./TrackingResult";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

function HeroIllustrationUnused() {
  return (
    <svg viewBox="0 0 620 460" className="w-full h-auto" xmlns="http://www.w3.org/2000/svg">
      {/* radar / target rings */}
      <g opacity="0.5">
        <circle cx="430" cy="120" r="120" fill="none" stroke="#EDEDED" strokeWidth="2" />
        <circle cx="430" cy="120" r="85" fill="none" stroke="#EDEDED" strokeWidth="2" />
        <circle cx="430" cy="120" r="48" fill="none" stroke="#EDEDED" strokeWidth="2" />
      </g>

      {/* clouds */}
      <g fill="#EAEFF5">
        <g className="kyo-float">
          <ellipse cx="360" cy="70" rx="34" ry="16" />
          <ellipse cx="388" cy="64" rx="22" ry="14" />
        </g>
        <g className="kyo-float-slow">
          <ellipse cx="500" cy="55" rx="30" ry="14" />
          <ellipse cx="525" cy="50" rx="18" ry="11" />
        </g>
      </g>

      {/* airplane */}
      <g className="kyo-float" transform="translate(300 95)">
        <path d="M0 18 L46 10 L70 14 L56 22 L44 20 L34 30 L24 30 L30 20 L10 22 Z" fill="#C9CED6" />
        <path d="M40 12 L60 8 L52 16 Z" fill="#ED2E24" />
      </g>

      {/* city skyline */}
      <g fill="#E6E9EE">
        <rect x="250" y="200" width="36" height="120" rx="3" />
        <rect x="292" y="170" width="30" height="150" rx="3" />
        <rect x="328" y="220" width="40" height="100" rx="3" />
        <rect x="430" y="185" width="32" height="135" rx="3" />
        <rect x="468" y="210" width="44" height="110" rx="3" />
      </g>
      <g fill="#F2C6C3" opacity="0.9">
        <rect x="258" y="212" width="8" height="8" /><rect x="272" y="212" width="8" height="8" />
        <rect x="300" y="186" width="7" height="8" /><rect x="312" y="186" width="7" height="8" />
        <rect x="438" y="200" width="8" height="8" /><rect x="476" y="224" width="8" height="8" />
      </g>

      {/* ground line */}
      <rect x="0" y="320" width="620" height="4" fill="#E6E9EE" />

      {/* cargo truck */}
      <g transform="translate(330 236)">
        <rect x="70" y="18" width="120" height="66" rx="6" fill="#FBB040" />
        <rect x="70" y="18" width="120" height="14" fill="#F5821F" />
        <path d="M0 40 L64 40 L64 84 L0 84 Z" fill="#ED2E24" />
        <rect x="8" y="48" width="34" height="22" rx="3" fill="#FCE3E1" />
        <circle cx="24" cy="90" r="12" fill="#333" /><circle cx="24" cy="90" r="5" fill="#ccc" />
        <circle cx="150" cy="90" r="12" fill="#333" /><circle cx="150" cy="90" r="5" fill="#ccc" />
      </g>

      {/* stacked packages */}
      <g transform="translate(150 262)">
        <rect x="0" y="30" width="52" height="48" rx="3" fill="#E39A6B" />
        <rect x="0" y="30" width="52" height="10" fill="#C97F52" />
        <rect x="18" y="30" width="16" height="48" fill="#D98F5F" opacity="0.6" />
        <rect x="56" y="0" width="44" height="78" rx="3" fill="#EDB07E" />
        <rect x="56" y="0" width="44" height="10" fill="#C97F52" />
        <rect x="72" y="0" width="14" height="78" fill="#D98F5F" opacity="0.6" />
      </g>

      {/* delivery person (orange) */}
      <g transform="translate(210 240)">
        <circle cx="18" cy="10" r="11" fill="#F5821F" />
        <path d="M4 26 Q18 18 32 26 L30 60 L6 60 Z" fill="#F5821F" />
        <rect x="6" y="60" width="9" height="20" fill="#3a3a3a" />
        <rect x="21" y="60" width="9" height="20" fill="#3a3a3a" />
        <rect x="30" y="30" width="26" height="18" rx="2" fill="#E39A6B" />
      </g>
    </svg>
  );
}

export default function Hero() {
  const [tracking, setTracking] = useState("");
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [data, setData] = useState(null);
  const navigate = useNavigate();

  // Tracking history — stored in localStorage, max 5 entries
  const [history, setHistory] = useState(() => {
    try { return JSON.parse(localStorage.getItem("kyo_tracking_history") || "[]"); }
    catch { return []; }
  });

  const saveToHistory = (tn) => {
    setHistory((prev) => {
      const updated = [tn, ...prev.filter((t) => t !== tn)].slice(0, 5);
      localStorage.setItem("kyo_tracking_history", JSON.stringify(updated));
      return updated;
    });
  };

  const doTrack = async (tn) => {
    setLoading(true);
    setError(null);
    setData(null);
    try {
      const res = await axios.get(`${API}/track/${encodeURIComponent(tn)}`);
      setData(res.data);
      saveToHistory(tn);
      toast.success("Package found");
    } catch (err) {
      const status = err?.response?.status;
      if (status === 404) {
        setError(`No package found for "${tn}". Please check the number and try again.`);
      } else {
        setError(err?.response?.data?.detail || "Unable to track right now. Please try again.");
      }
    } finally {
      setLoading(false);
    }
  };

  const handleTrack = (e) => {
    e.preventDefault();
    const tn = tracking.trim();
    if (!tn) { toast.error("Please enter a tracking number"); return; }
    doTrack(tn);
  };

  const handleHistoryClick = (tn) => {
    setTracking(tn);
    doTrack(tn);
  };

  return (
    <section id="home" className="pt-[68px] relative overflow-hidden">
      <div className="max-w-[1240px] mx-auto px-5 lg:px-6">
        <div className="grid lg:grid-cols-2 gap-8 items-center min-h-[560px] py-10">
          <div className="relative z-10">
            <h1 className="text-[44px] sm:text-[56px] lg:text-[64px] font-light leading-[1.1] tracking-tight text-[#2d2d2d]" style={{ fontFamily: "'Raleway', sans-serif", fontWeight: 300 }}>
              Welcome to<br /><span className="text-[#ED2E24]">KYo</span> Group
            </h1>
            <button
              onClick={() => navigate("/signup")}
              className="mt-6 bg-[#ED2E24] hover:bg-[#c9231a] text-white font-semibold px-8 py-2.5 rounded-md transition-colors shadow-sm"
              data-testid="hero-signup-btn"
            >
              Sign Up
            </button>

            <p className="mt-8 text-[18px] font-semibold text-[#1a1a1a]">Track your package</p>
            <form
              onSubmit={handleTrack}
              className="mt-3 flex w-full max-w-[440px] rounded-md overflow-hidden border border-gray-200 shadow-sm bg-white"
              data-testid="tracking-form"
            >
              <input
                value={tracking}
                onChange={(e) => setTracking(e.target.value)}
                placeholder="Enter tracking number e.g. KN123456789"
                className="flex-1 min-w-0 px-4 py-3 text-[13px] text-[#1a1a1a] placeholder:text-gray-400 outline-none"
                data-testid="tracking-input"
              />
              <button
                type="submit"
                disabled={loading}
                className="shrink-0 whitespace-nowrap bg-[#ED2E24] hover:bg-[#c9231a] disabled:opacity-70 text-white text-[14px] font-semibold px-4 sm:px-8 transition-colors"
                data-testid="tracking-submit-btn"
              >
                {loading ? "…" : "Track"}
              </button>
            </form>

            {/* Recent tracking history */}
            {history.length > 0 && (
              <div className="mt-3 max-w-[440px]" data-testid="tracking-history">
                <p className="text-[11px] font-medium text-gray-400 mb-1.5 uppercase tracking-wide">
                  Recent searches
                </p>
                <div className="flex flex-wrap gap-2">
                  {history.map((tn) => (
                    <button
                      key={tn}
                      onClick={() => handleHistoryClick(tn)}
                      className="text-xs bg-gray-100 hover:bg-red-50 hover:text-[#ED2E24] hover:border-red-200 text-gray-600 border border-gray-200 px-3 py-1.5 rounded-full transition-all font-mono"
                      data-testid={`history-chip-${tn}`}
                    >
                      {tn}
                    </button>
                  ))}
                </div>
              </div>
            )}
          </div>

          <div className="relative">
            <Lottie
              src={kyoAnimation}
              loop
              autoplay
              className="w-full h-auto"
            />
          </div>
        </div>

        {(loading || error || data) && (
          <div className="pb-14 -mt-2">
            <TrackingResult loading={loading} error={error} data={data} />
          </div>
        )}
      </div>
    </section>
  );
}
